import { combineReducers, configureStore, getDefaultMiddleware } from '@reduxjs/toolkit'
import { AppReducer } from './slices/app-slice'
import { DisplayFightReducer } from './slices/display-fight-slice'
import { EditSetupReducer } from './slices/edit-setup-slice'
import { EditFightReducer } from './slices/edit-fight-slice'
import { SetupReducer } from './slices/setup-slice'
import { FightReducer } from './slices/fight-slice'
import { InteractivityReducer } from './slices/interactivity-slice'
import { ModalActions, ModalReducer } from './slices/modal-slice'
import { RosterReducer } from './slices/roster-slice'
import { MetaReducer } from './slices/meta-slice'
import { Storage } from 'web/utils/local-storage'
import { useDispatch } from 'react-redux'
import { metaMiddleware } from './middleware/meta-middleware'
import { assertDefined, expectOneById } from 'web/utils/utils'

const rootReducer = combineReducers({
    app: AppReducer,
    fight: FightReducer,
    editFight: EditFightReducer,
    modal: ModalReducer,
    roster: RosterReducer,

    setup: SetupReducer,
    editSetup: EditSetupReducer,

    displayFight: DisplayFightReducer,
    interactivity: InteractivityReducer,
    meta: MetaReducer,
})

export type RootState = ReturnType<typeof rootReducer>

export const store = configureStore({
    reducer: rootReducer,
    preloadedState: tryLoadState(),
    middleware: getDefaultMiddleware({
        serializableCheck: {
            ignoredActions: [
                // We have to ignore these actions because they take
                // non-serializable functions as input, so RTK will complain.
                // ModalActions.openPromptModal.type,
                // ModalActions.openConfirmModal.type,
                // ModalActions.openEditPlayerModal.type,
                // ModalActions.openTextAreaModal.type,
                // ModalActions.openImportFightModal.type,
                // ModalActions.openTabbedExportModal.type,
                ...Object.values(ModalActions).map(v => v.type)
            ]
        }
    }).concat(
        metaMiddleware
    ),
})

type AppDispatch = typeof store.dispatch

/**
 * This is the dispatcher we have to use if we want to be able to
 * chain off of thunk actions.
 */
export const useAppDispatch = () => useDispatch<AppDispatch>()

function tryLoadState(): Partial<RootState> {
    const storedState = Storage.readState()

    const state: Partial<RootState> = {
        meta: storedState.meta,
        fight: storedState.fight,
        setup: storedState.setups,
        roster: storedState.roster
    }

    // This is part of the setup-recovery system. If all of this data
    // is found, we're going to attempt a recovery of the last stored
    // setup data that was being edited.
    //
    // We're effectively just taking both state-slices and serializing
    // them into the meta-slice for data transfer. Then we unpack them
    // in the UI and if the user wants to do a recovery we parse them
    // and push them into their respective slices again.
    //
    // This should all be fairly safe as long as the user doesn't get
    // the chance to fiddle with any of the underlying setup/fight
    // data, which we block by instantly displaying a modal when the
    // user loads the application and there is recovery data.
    //
    // NOTICE: One slightly unintuitive detail about this
    // implementation is that we technically store this recovery data
    // in 2 places. First we store it in the `editFight` and
    // `editSetup` fields right here, but once we've loaded it, it
    // will also be stored as JSON in the meta slice.
    //
    // This allows us to retain the recovery data until the user takes
    // action on it, even though we resume application loading and
    // start saving state to localStorage normally, which will delete
    // any recovery data stored in these 2 fields.
    if (state.meta && storedState.editFight && storedState.editSetup) {

        // If we error in this first portion, we have nothing catching
        // it, the application will just white-screen and all the
        // information will be hidden in the console.
        //
        // To fix this we'd have to rework the entire loading
        // pipeline, as right now, if I exit out of here without a
        // throw, the application will try to spin up normally and our
        // state will most likely be completely lost.
        //
        // We'd have to set some global fatal-error flag here, and
        // then very carefully check that flag along the pipeline to
        // make sure we stop all execution of data-writing to disk etc
        // etc and simply error out.

        assertDefined(storedState.editFight.fight)
        assertDefined(storedState.editSetup.setup)

        assertDefined(storedState.fight?.fights)
        assertDefined(storedState.setups?.setups)

        const fightId = storedState.editSetup.fightId
        const setupId = storedState.editSetup.setup.id

        assertDefined(fightId)
        assertDefined(setupId)

        // This is the only expected error scenario, if anything else
        // blows up something is very wrong and we should bring the
        // entire app down and try to maintain the recovery data.
        try {
            expectOneById(storedState.fight.fights, fightId)
            expectOneById(storedState.setups.setups, setupId)

            state.meta.setupRecovery = {
                fightId: storedState.editFight.fight.id,
                setupId: storedState.editSetup.setup.id,
                editFightJson: JSON.stringify(storedState.editFight),
                editSetupJson: JSON.stringify(storedState.editSetup),
            }
        } catch (error) {
            console.error(error)
            console.error("Failed to restore a setup-recovery because either the affected setup or fight has been removed since the recovery was written, the recovery data will be silently ignored")
        }

    }

    return state
}

/**
 * Write redux to localStorage on change
 */
store.subscribe(() => {
    const {
        meta,
        fight,
        setup: setups,
        roster,
        editFight,
        editSetup
    } = store.getState()

    // This branch will only activate while we're in setup-edit mode.
    // As soon as we leave it via the UI, the data will and should be
    // removed.
    //
    // This is part of the setup-recovery system in case anything
    // happens while a setup is being edited and the user doesn't exit
    // edit mode via the UI.
    if (editFight.fight && editSetup.setup) {

        Storage.writeState({
            fight,
            setups,
            roster,
            meta,
            editFight,
            editSetup
        })

    } else {

        Storage.writeState({
            fight,
            setups,
            roster,
            meta,
            editFight: undefined,
            editSetup: undefined
        })

    }

})

/**
 * This takes care of the first write of the day.
 *
 * This will wipe any setup-recovery data that was previously stored.
 * At this point, if the user has yet to take action on the recovery
 * modal, the data is still safely stored in the meta-slice instead
 * and will safely be loaded from there until the user takes action on
 * it.
 */
const { fight, setup: setups, roster, meta } = store.getState()

Storage.writeState({
    fight,
    setups,
    roster,
    meta
})
